import { BellOutlined } from '@ant-design/icons';
import {
  Badge,
  Box,
  Button,
  ClickAwayListener,
  Grid,
  List,
  ListItemButton,
  Paper,
  Popper,
  Stack,
  Typography,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import IconButton from 'components/@extended/IconButton';
import Transitions from 'components/@extended/Transitions';
import MainCard from 'components/MainCard';
import { dateOptions } from 'constants/dateFormats';
import useSetPath from 'hooks/data-saving/useSetPath';
import useAuth from 'hooks/useAuth';
import { useRef, useState } from 'react';
import { RootState, useSelector } from 'store';
import { ThemeMode } from 'types/config';
import { timeOptionShort } from 'utils/staticOptions';

// ==============================|| HEADER CONTENT - NOTIFICATION ||============================== //

const Notification = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const matchesXs = useMediaQuery(theme.breakpoints.down('md'));
  const { unread } = useSelector((state: RootState) => state.notifications);
  const setPath = useSetPath();

  const anchorRef = useRef<any>(null);
  const [open, setOpen] = useState(false);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleMarkDone = (id: string) => {
    setPath(`contacts/${user?.contactId}/notifications/${id}`, { status: 'done' });
  };

  const iconBackColorOpen = theme.palette.mode === ThemeMode.DARK ? 'grey.200' : 'grey.300';
  const iconBackColor = theme.palette.mode === ThemeMode.DARK ? 'background.default' : 'grey.100';

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <IconButton
        color="secondary"
        variant="light"
        sx={{ color: 'text.primary', bgcolor: open ? iconBackColorOpen : iconBackColor }}
        aria-label="open profile"
        ref={anchorRef}
        aria-controls={open ? 'profile-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <Badge badgeContent={unread.length} color="primary">
          <BellOutlined />
        </Badge>
      </IconButton>
      <Popper
        placement={matchesXs ? 'bottom' : 'bottom-end'}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [matchesXs ? -5 : 0, 9]
              }
            }
          ]
        }}
      >
        {({ TransitionProps }) => (
          <Transitions
            type="grow"
            position={matchesXs ? 'top' : 'top-right'}
            sx={{ overflow: 'hidden' }}
            in={open}
            {...TransitionProps}
          >
            <Paper
              sx={{
                boxShadow: theme.customShadows.z1,
                width: '100%',
                minWidth: 400,
                maxWidth: 500,
                [theme.breakpoints.down('md')]: {
                  maxWidth: 285
                }
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard elevation={0} border={false} content={false}>
                  <List component="nav">
                    {unread.map((item, index) => (
                      <ListItemButton key={index}>
                        <Grid container spacing={1}>
                          <Grid item xs={10}>
                            <Stack>
                              <Typography variant="subtitle1">{item.subject}</Typography>
                              <Typography variant="body2" sx={{ color: 'GrayText' }}>
                                {`${item.timestamp?.toLocaleDateString(
                                  [],
                                  dateOptions
                                )} ${item.timestamp?.toLocaleTimeString([], timeOptionShort)}`}
                              </Typography>
                              <Typography variant="body2">{item.html}</Typography>
                            </Stack>
                          </Grid>
                          <Grid item xs={2}>
                            <Button
                              variant="outlined"
                              onClick={() => item.id && handleMarkDone(item.id)}
                              size="extraSmall"
                              sx={{ m: 1 }}
                            >
                              Done
                            </Button>
                          </Grid>
                        </Grid>
                      </ListItemButton>
                    ))}
                    {unread.length === 0 && (
                      <ListItemButton sx={{justifyContent: 'center'}} >
                        <Typography>No notifications at the moment</Typography>
                      </ListItemButton>
                    )}
                    {/* <Divider /> */}
                  </List>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
};

export default Notification;
